<template>
	<div class="ops-navbar-switcher" :class="{ disabled: isDisabled }">
		<div class="tabs-container">
			<div 
				v-for="(item, index) in visibleItems" 
				:key="item.value"
				class="tab-item"
				:class="{ active: isActive(item.value) }"
				@click="handleSwitch(item.value)"
			>
				<div class="tab-content">
					<div class="tab-icon" v-html="getIcon(item.icon)"></div>
					<span class="tab-label">{{ item.label }}</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { useUserStore, useOrdersStore, useRoomsStore } from "@/console-new/stores";
import { useLanguageStore, useSvgStore } from "@/shared/stores";

export default {
	setup() {
		const userStore = useUserStore();
		const ordersStore = useOrdersStore();
		const languageStore = useLanguageStore();
		const svgStore = useSvgStore();
		const roomsStore = useRoomsStore();
		return {
			userStore,
			ordersStore,
			roomsStore,
			getString: languageStore.getString,
			getIcon: svgStore.get
		};
	},
	name: "OpsNavbarSwitcher",
	props: {
		sectionsShown: {
			type: String,
			required: true,
			validator(value) {
				return ['counter', 'orders', 'all'].includes(value);
			}
		}
	},
	computed: {
		isDisabled() {
			return this.userStore.requirePin && !this.userStore.unlockUserId;
		},
		activeSection() {
			const path = this.$route.path;
			if (path === '/ops/counter') {
				return 'counter';
			} else if (path === '/ops/tables' || path === '/ops/tables/reservations') {
				return 'tables';
			} else if (path === '/ops/orders') {
				return 'orders';
			}
			return 'orders';
		},
		visibleItems() {
			const items = [];
			
			if (this.sectionsShown === 'counter') {
				items.push(
					{ value: 'counter', label: this.getString('sale'), icon: 'iconCounterSales' },
					{ value: 'orders', label: this.getString('orders'), icon: 'iconAnalyticsOrders' }
				);
			} else if (this.sectionsShown === 'orders') {
				items.push(
					{ value: 'tables', label: this.getString('rooms'), icon: 'iconAnalyticsTable' },
					{ value: 'orders', label: this.getString('orders'), icon: 'iconAnalyticsOrders' }
				);
			} else if (this.sectionsShown === 'all') {
				items.push({ value: 'counter', label: this.getString('sale'), icon: 'iconCounterSales' });

				if (this.roomsStore.rooms?.length > 0) {
					items.push({ value: 'tables', label: this.getString('rooms'), icon: 'iconAnalyticsTable' });
				}

				items.push({ value: 'orders', label: this.getString('orders'), icon: 'iconAnalyticsOrders' });
			}
			
			return items;
		}
	},
	methods: {
		isActive(value) {
			return this.activeSection === value;
		},
		handleSwitch(value) {
			if (this.isDisabled) {
				return;
			}
			
			const currentPath = this.$route.path;
			let targetPath = '/ops/orders';
			
			if (value === 'counter') {
				targetPath = '/ops/counter';
			} else if (value === 'tables') {
				targetPath = '/ops/tables';
			} else if (value === 'orders') {
				targetPath = '/ops/orders';
			}
			
			if (currentPath !== targetPath) {
				if (value !== 'orders') {
					this.ordersStore.resetOrders();
				}
				this.$router.push(targetPath);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.ops-navbar-switcher {
	display: flex;
	align-items: center;
	flex: 1;
	height: 100%;
	
	&.disabled {
		opacity: 0.5;
		pointer-events: none;
	}
	
	.tabs-container {
		display: flex;
		flex: 1;
		gap: 4px;
		align-items: center;
		height: 46px;
		border-radius: 13px;
		
		.tab-item {
			flex: 1;
			height: 100%;
			border-radius: 13px;
			background-color: var(--color-bg-primary-5);
			transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			box-shadow: inset 0 0 0 0 var(--color-border);

			&.active {
				background-color: var(--color-secondary);
				box-shadow: 2px 2px 10px 0 var(--color-primary-alpha-4) inset;
			}
			
			.tab-content {
				display: flex;
				align-items: center;
				gap: 6px;
				padding: 0 12px;
				
				.tab-icon {
					width: 16px;
					height: 16px;
					display: flex;
					align-items: center;
					justify-content: center;
					
					:deep(svg) {
						width: 100%;
						height: 100%;
						
						.fill {
							fill: var(--color-primary);
							transition: fill 0.3s ease;
						}
						
						.stroke {
							stroke: var(--color-primary);
							transition: stroke 0.3s ease;
						}
					}
				}
				
				.tab-label {
					font-size: 13px;
					font-weight: 500;
					color: var(--color-primary);
					white-space: nowrap;
					transition: color 0.3s ease;
					user-select: none;
				}
			}
		}
	}
	
	@media (max-width: 768px) {
		padding: 0 4px;
		
		.tabs-container {
			.tab-item {
				.tab-content {
					padding: 0 8px;
					
					.tab-icon {
						width: 14px;
						height: 14px;
					}
					
					.tab-label {
						font-size: 12px;
					}
				}
			}
		}
	}
	
	@media (max-width: 480px) {
		.tabs-container {
			.tab-item {
				.tab-content {
					padding: 0 6px;

					.tab-label {
						font-size: 11px;
					}
				}
			}
		}
	}
	
	@media (max-width: 420px) {
		.tabs-container {
			.tab-item {
				.tab-content {
					.tab-icon {
						display: none;
					}
				}
			}
		}
	}
}
</style>